/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import classNames from 'classnames';

import { isUserRightToLeft } from 'js/lib/language';
import { useNavigate } from 'js/lib/useRouter';

import type { Theme } from '@coursera/cds-core';
import { typography2, useTheme } from '@coursera/cds-core';
import { ChevronNextIcon } from '@coursera/cds-icons';

import type { Subdomain } from 'bundles/browse-common/legacy/components/Domain/types/SubDomain';
import type { Domain } from 'bundles/browse/components/Domain/types/Domain';
import { BROWSE_ROOT_URL } from 'bundles/browse/constants';

import _t from 'i18n!nls/browse';

const styles = {
  breadcrumbs: (theme: Theme) => css`
    .rc-Breadcrumbs {
      position: relative;

      p {
        display: none;
      }

      ol {
        margin: 0;
        padding: 0;

        > li {
          list-style: none;
          display: inline-flex;
          color: var(--cds-color-neutral-primary-invert);

          svg {
            vertical-align: middle;
            margin: 3px 12px;
          }

          &.isRTL svg {
            -webkit-transform: scaleX(-1);
            -moz-transform: scaleX(-1);
            -o-transform: scaleX(-1);
            transform: scaleX(-1);
            filter: fliph;
            -ms-filter: 'FlipH';
          }
        }
      }

      .breadcrumb-link {
        color: var(--cds-color-neutral-primary-invert);
        ${typography2.bodySecondary}
      }

      a {
        display: inline;

        &:hover {
          color: var(--cds-color-neutral-primary-invert);
        }
      }

      .is-current {
        ${typography2.bodySecondary}
        color: var(--cds-color-neutral-primary-invert);
      }
    }
  `,
};

type Props = {
  domain?: Domain;
  subdomain?: Subdomain;
};

export const Breadcrumbs = ({ domain, subdomain }: Props) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const breadcrumbs = [{ label: _t('Explore'), href: BROWSE_ROOT_URL }];
  if (domain?.name) {
    breadcrumbs.push({
      label: domain.name,
      href: `${BROWSE_ROOT_URL}/${domain.slug || domain.id}`,
    });

    if (subdomain?.name) {
      breadcrumbs.push({
        label: subdomain.name,
        href: `${BROWSE_ROOT_URL}/${domain.slug || domain.id}/${subdomain.id}`,
      });
    }
  }
  return (
    <div css={styles.breadcrumbs(theme)}>
      <nav className="rc-Breadcrumbs" aria-label={_t('Breadcrumbs')} data-e2e="browse-breadcrumb">
        <ol id="breadcrumb">
          {breadcrumbs.map(({ label, href }, idx) => {
            const isCurrent = idx === breadcrumbs.length - 1;
            return isCurrent ? (
              <li
                key={label}
                className="nostyle breadcrumb-link is-current"
                aria-current="page"
                data-e2e="current-breadcrumb"
              >
                {label}
              </li>
            ) : (
              <li key={label} className={classNames('not-current', { isRTL: isUserRightToLeft() })}>
                <a
                  href={href}
                  className={classNames('nostyle breadcrumb-link', { isCurrent })}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={(event) => {
                    event?.preventDefault();
                    navigate(href);
                  }}
                  data-e2e="breadcrumb-link"
                >
                  {label}
                </a>
                <ChevronNextIcon size="small" />
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
};

export default Breadcrumbs;
